import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Target, BarChart3, CheckCircle2, ArrowRight, Clock, Eye, Edit, ChevronDown, ChevronUp } from "lucide-react";
import { API } from "./shared/utils";
import axios from "axios";

const SelfAssessmentWidget = ({ currentUser }) => {
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [showAssessment, setShowAssessment] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [result, setResult] = useState(null);
  const [pastResponses, setPastResponses] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [viewingResponse, setViewingResponse] = useState(null);
  const [editingResponse, setEditingResponse] = useState(null);

  useEffect(() => {
    if (currentUser?.id) fetchAssessments();
  }, [currentUser?.id]);

  const fetchAssessments = async () => {
    try {
      const res = await axios.get(`${API}/assessments`);
      setAssessments(res.data);
    } catch (err) {
      console.error("Error fetching assessments:", err);
    }
  };

  const startAssessment = async (assessment) => {
    try {
      const res = await axios.get(`${API}/assessments/${assessment.id}`);
      setSelectedAssessment(res.data);
      setQuestions(res.data.questions || []);
      setAnswers({});
      setResult(null);
      setShowResults(false);
      setEditingResponse(null);
      setShowAssessment(true);
    } catch (err) {
      console.error("Error loading assessment:", err);
    }
  };

  const editPastResponse = async (response, assessment) => {
    try {
      const res = await axios.get(`${API}/assessments/${assessment.id}`);
      setSelectedAssessment(res.data);
      setQuestions(res.data.questions || []);
      setAnswers(response.answers || {});
      setResult(null);
      setShowResults(false);
      setEditingResponse(response);
      setShowHistory(false);
      setShowAssessment(true);
    } catch (err) {
      console.error("Error loading assessment for edit:", err);
    }
  };

  const submitAssessment = async () => {
    if (Object.keys(answers).length < questions.length) {
      alert("Please answer all questions before submitting.");
      return;
    }
    try {
      let res;
      if (editingResponse) {
        // Update existing response
        res = await axios.put(`${API}/assessments/responses/${editingResponse.id}`, {
          answers
        });
      } else {
        // Create new response
        res = await axios.post(`${API}/assessments/${selectedAssessment.id}/submit`, {
          user_id: currentUser.id,
          answers
        });
      }
      setResult(res.data);
      setShowResults(true);
      setEditingResponse(null);
    } catch (err) {
      console.error("Error submitting:", err);
      alert("Error submitting assessment.");
    }
  };

  const viewHistory = async (assessmentId) => {
    try {
      const [respRes, assessRes] = await Promise.all([
        axios.get(`${API}/assessments/${assessmentId}/responses?user_id=${currentUser.id}`),
        axios.get(`${API}/assessments/${assessmentId}`)
      ]);
      setPastResponses(respRes.data);
      setQuestions(assessRes.data.questions || []);
      setSelectedAssessment(assessRes.data);
      setViewingResponse(null);
      setShowHistory(true);
    } catch (err) {
      console.error("Error fetching history:", err);
    }
  };

  const getScoreColor = (score, max) => {
    const pct = (score / max) * 100;
    if (pct >= 80) return "text-green-400";
    if (pct >= 60) return "text-amber-400";
    if (pct >= 40) return "text-orange-400";
    return "text-red-400";
  };

  const getScoreBg = (score, max) => {
    const pct = (score / max) * 100;
    if (pct >= 80) return "from-green-500/20 to-emerald-500/20 border-green-500/30";
    if (pct >= 60) return "from-amber-500/20 to-yellow-500/20 border-amber-500/30";
    if (pct >= 40) return "from-orange-500/20 to-red-500/20 border-orange-500/30";
    return "from-red-500/20 to-pink-500/20 border-red-500/30";
  };

  const getScaleLabel = (val) => {
    const labels = { 1: "Strongly Disagree", 2: "Disagree", 3: "Neutral", 4: "Agree", 5: "Strongly Agree" };
    return labels[val] || val;
  };

  if (!currentUser) return null;

  return (
    <>
      {/* Assessment Widget Card */}
      <Card className="bg-gradient-to-br from-slate-800/90 to-indigo-900/40 border-indigo-500/30 shadow-xl backdrop-blur-sm" data-testid="assessment-widget">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-white">
            <Target className="w-5 h-5 text-indigo-400" />
            Self-Assessment
          </CardTitle>
          <CardDescription className="text-slate-400">
            Evaluate your skills and track your growth
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {assessments.length === 0 ? (
            <div className="text-center py-4 bg-slate-900/30 rounded-lg border border-slate-700/50">
              <Target className="w-8 h-8 mx-auto mb-2 text-slate-600" />
              <p className="text-slate-400 text-sm">No assessments available yet</p>
            </div>
          ) : assessments.map((assessment) => (
            <div key={assessment.id} className="p-3 rounded-lg bg-slate-900/50 border border-slate-700/50 hover:border-indigo-500/40 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">{assessment.title}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{assessment.description}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => viewHistory(assessment.id)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs h-7"
                    data-testid="view-history-btn"
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    History
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => startAssessment(assessment)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7"
                    data-testid="take-assessment-btn"
                  >
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Take
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Assessment Dialog */}
      <Dialog open={showAssessment} onOpenChange={setShowAssessment}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">
              {showResults ? "Your Results" : editingResponse ? "Update Assessment" : selectedAssessment?.title}
            </DialogTitle>
          </DialogHeader>

          {!showResults ? (
            <div className="space-y-4">
              {editingResponse && (
                <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
                  <p className="text-amber-300 text-xs">Updating your previous assessment. Change your answers and resubmit.</p>
                </div>
              )}

              {/* Scale Legend */}
              <div className="flex items-center justify-center gap-4 p-2 rounded-lg bg-slate-800/50 border border-slate-700/50">
                <span className="text-xs text-slate-400">1 = Strongly Disagree</span>
                <span className="text-xs text-slate-500">|</span>
                <span className="text-xs text-slate-400">5 = Strongly Agree</span>
              </div>

              {/* Questions */}
              <div className="space-y-4">
                {questions.map((q, idx) => (
                  <div key={q.id} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                    <p className="text-white text-sm mb-3">
                      <span className="text-indigo-400 font-bold mr-2">{idx + 1}.</span>
                      {q.question_text}
                    </p>
                    <div className="flex gap-2 justify-center">
                      {[1, 2, 3, 4, 5].map((val) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setAnswers({ ...answers, [q.id]: val })}
                          className={`w-10 h-10 rounded-full font-bold text-sm transition-all ${
                            answers[q.id] === val
                              ? "bg-indigo-500 text-white scale-110 ring-2 ring-indigo-400/50"
                              : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                          }`}
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Progress + Submit */}
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-slate-400">
                  {Object.keys(answers).length}/{questions.length} answered
                </span>
                <Button
                  onClick={submitAssessment}
                  disabled={Object.keys(answers).length < questions.length}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  data-testid="submit-assessment-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {editingResponse ? "Update Assessment" : "Submit Assessment"}
                </Button>
              </div>
            </div>
          ) : (
            /* Results View */
            <div className="space-y-5">
              {/* Score Card */}
              <div className={`p-6 rounded-xl bg-gradient-to-br ${getScoreBg(result.total_score, result.max_score)} border text-center`}>
                <p className={`text-5xl font-bold ${getScoreColor(result.total_score, result.max_score)}`}>
                  {result.total_score}
                </p>
                <p className="text-slate-400 text-sm mt-1">out of {result.max_score}</p>
                <div className="w-full bg-slate-700/50 rounded-full h-2 mt-3">
                  <div
                    className="h-2 rounded-full bg-indigo-500 transition-all duration-700"
                    style={{ width: `${(result.total_score / result.max_score) * 100}%` }}
                  />
                </div>
              </div>

              {/* Interpretation */}
              <div className="p-4 rounded-lg bg-slate-800/50 border border-indigo-500/20">
                <p className="text-white font-medium text-center">{result.interpretation}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResults(false);
                    setShowAssessment(false);
                  }}
                  className="border-slate-600 text-slate-300"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setAnswers({});
                    setResult(null);
                    setShowResults(false);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Retake Assessment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-400" />
              Assessment History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {pastResponses.length === 0 ? (
              <div className="text-center py-8">
                <Target className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-400">No attempts yet. Take your first assessment!</p>
              </div>
            ) : (
              pastResponses.map((resp, idx) => {
                const isExpanded = viewingResponse === resp.id;
                return (
                  <div key={resp.id} className="rounded-lg bg-slate-800/50 border border-slate-700/50 overflow-hidden">
                    {/* Summary Row */}
                    <div className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setViewingResponse(isExpanded ? null : resp.id)}
                          className="text-slate-400 hover:text-white transition-colors"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <div>
                          <p className="text-white text-sm font-medium">
                            Attempt #{pastResponses.length - idx}
                          </p>
                          <p className="text-slate-400 text-xs">
                            {new Date(resp.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className={`text-lg font-bold ${getScoreColor(resp.total_score, resp.max_score)}`}>
                            {resp.total_score}/{resp.max_score}
                          </p>
                          <p className="text-xs text-slate-500">{Math.round((resp.total_score / resp.max_score) * 100)}%</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => editPastResponse(resp, assessments.find(a => a.id === resp.assessment_id) || assessments[0])}
                          className="border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/20 h-7 text-xs"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Update
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Answer Details */}
                    {isExpanded && (
                      <div className="border-t border-slate-700/50 p-3 space-y-2 bg-slate-900/30">
                        <p className="text-xs text-slate-500 font-medium mb-2">Your Answers:</p>
                        {questions.length > 0 ? (
                          questions.map((q, qIdx) => {
                            const val = resp.answers?.[q.id];
                            return (
                              <div key={q.id} className="flex items-start gap-2 text-sm">
                                <span className="text-indigo-400 font-bold text-xs mt-0.5 w-5 flex-shrink-0">{qIdx + 1}.</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-slate-300 text-xs">{q.question_text}</p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {[1, 2, 3, 4, 5].map((v) => (
                                    <div key={v} className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold ${
                                      val === v ? 'bg-indigo-500 text-white' : 'bg-slate-700/50 text-slate-500'
                                    }`}>{v}</div>
                                  ))}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          /* If questions aren't loaded, show raw answers */
                          Object.entries(resp.answers || {}).map(([qId, val], i) => (
                            <div key={qId} className="flex justify-between text-xs">
                              <span className="text-slate-400">Question {i + 1}</span>
                              <span className={`font-bold ${val >= 4 ? 'text-green-400' : val >= 3 ? 'text-amber-400' : 'text-red-400'}`}>
                                {val}/5 — {getScaleLabel(val)}
                              </span>
                            </div>
                          ))
                        )}
                        <div className="pt-2 border-t border-slate-700/30">
                          <p className="text-xs text-slate-500 italic">{resp.interpretation}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SelfAssessmentWidget;
